import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { CUSTOM_PLAN_TEXT, TalkToUsModal } from '../components/CustomPlanCard';
import { ExternalLinkIcon } from '../components/ExternalLinkIcon';
import { useAuth } from '../lib/auth';
import { DOCS_URL } from '../lib/chains';
import { isIntentPlan } from '../lib/intent';
import { compact, count, price } from '../lib/money';
import { proofPriceRows, usePrices, type PriceList } from '../lib/prices';
import type { RpcPlan } from '../lib/rpc-client';

/**
 * The public price list, mirroring willow.tech/pricing (willow-website
 * src/components/PricingPage.tsx). Keep the words in step with that page.
 */

type Bullet = { text: string; off?: boolean };

interface Card {
  plan: RpcPlan;
  price: string;
  period: string;
  note?: string;
  blurb: string;
  bullets: Bullet[];
  /** The button's words for someone without an account yet. */
  cta: string;
}

const PLAN_ORDER = ['free', 'payg', 'builder', 'scale'];

const rateLine = (p: RpcPlan): Bullet => ({
  text: `${count(p.requests_per_second)} requests a second`,
});

const stateLine = (p: RpcPlan): Bullet =>
  p.state_methods ? { text: 'State methods' } : { text: 'No state methods', off: true };

function cardFor(p: RpcPlan, prices: PriceList): Card {
  if (p.id === 'free') {
    return {
      plan: p,
      price: price(0),
      period: 'no card needed',
      blurb: 'For trying it out.',
      bullets: [
        { text: `${compact(p.included_units)} units a month` },
        rateLine(p),
        p.proofs ? { text: 'Log proofs' } : { text: 'No log proofs', off: true },
        stateLine(p),
      ],
      cta: 'Get an API key',
    };
  }
  if (p.id === 'payg') {
    return {
      plan: p,
      price: p.unit_price_micros_per_million === null ? '' : price(p.unit_price_micros_per_million),
      period: 'per 1M units',
      note: `Prepaid from ${price(prices.credits.min_topup_micros ?? 0)}`,
      blurb: 'No monthly fee.',
      bullets: [rateLine(p), { text: 'Log proofs' }, stateLine(p)],
      cta: 'Get an API key',
    };
  }
  const then =
    p.unit_price_micros_per_million === null
      ? ''
      : `, then ${price(p.unit_price_micros_per_million)} per 1M`;
  return {
    plan: p,
    price: price(p.monthly_micros),
    period: '/ month',
    blurb: p.id === 'builder' ? 'For an app in production.' : 'For heavy indexing and analytics.',
    bullets: [
      { text: `${compact(p.included_units)} units a month${then}` },
      rateLine(p),
      { text: `${price(p.proof_credit_micros)} of log proofs a month` },
      stateLine(p),
    ],
    cta: `Choose ${p.display_name}`,
  };
}

function cards(prices: PriceList): Card[] {
  return PLAN_ORDER.flatMap((id) => {
    const p = prices.plans.find((plan) => plan.id === id);
    return p ? [cardFor(p, prices)] : [];
  });
}

/** Signed out, a plan is picked by signing up. Signed in, it's picked on the
 *  billing page, which reads the same `plan` parameter. Free has nothing to
 *  pick up, so it carries none. */
function ctaFor(card: Card, signedIn: boolean): { label: string; to: string } {
  const plan = isIntentPlan(card.plan.id) ? `?plan=${card.plan.id}` : '';
  return signedIn
    ? { label: `Choose ${card.plan.display_name}`, to: `/billing${plan}` }
    : { label: card.cta, to: `/signup${plan}` };
}

export function PricingPanel() {
  const { user } = useAuth();
  // The snapshot renders first; the live list from /v1/prices replaces it.
  const prices = usePrices();
  const [talking, setTalking] = useState(false);
  const closeTalk = useCallback(() => setTalking(false), []);

  useEffect(() => {
    const previous = document.title;
    document.title = 'Pricing — Willow Dashboard';
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <div className="pricing-page" data-testid="pricing-page" data-price-source={prices.source}>
      <section className="pricing-section">
        <div className="container">
          <header className="pricing-head">
            <div className="panel-eyebrow pricing-eyebrow">Pricing</div>
            <h1 className="pricing-title">Start free. Pay for what you use.</h1>
            <p className="pricing-lead">
              Every plan serves the same verified data on all seven chains.
            </p>
          </header>

          <div className="pricing-grid">
            {cards(prices).map((c) => {
              const cta = ctaFor(c, !!user);
              return (
                <div key={c.plan.id} className="pricing-card" data-testid={`pricing-plan-${c.plan.id}`}>
                  <div className="pricing-name">{c.plan.display_name}</div>
                  <div className="pricing-price">
                    <span className="pricing-amount">{c.price}</span>
                    <span className="pricing-period">{c.period}</span>
                  </div>
                  {c.note && <div className="pricing-note">{c.note}</div>}
                  <p className="pricing-blurb">{c.blurb}</p>
                  <ul className="pricing-bullets">
                    {c.bullets.map((b) => (
                      <li key={b.text} className={b.off ? 'is-unavailable' : undefined}>
                        {b.text}
                      </li>
                    ))}
                  </ul>
                  <Link
                    className={`btn btn-lg pricing-cta${c.plan.id === 'free' ? ' btn-primary' : ''}`}
                    to={cta.to}
                    data-testid={`pricing-cta-${c.plan.id}`}
                  >
                    {cta.label}
                  </Link>
                </div>
              );
            })}

            <div className="pricing-card pricing-card--custom" data-testid="pricing-plan-custom">
              <div>
                <div className="pricing-name">Custom</div>
                <p className="pricing-blurb">{CUSTOM_PLAN_TEXT}</p>
              </div>
              <button
                className="btn btn-lg pricing-cta"
                type="button"
                onClick={() => setTalking(true)}
                data-testid="pricing-talk"
              >
                Talk to us
              </button>
            </div>
          </div>

          <p className="pricing-footnote">
            Prices are in US dollars. The{' '}
            <a href={`${DOCS_URL}/pricing/`} target="_blank" rel="noreferrer">
              <ExternalLinkIcon>docs</ExternalLinkIcon>
            </a>{' '}
            explain units, rate limits, and billing.
          </p>
        </div>
      </section>

      <section className="pricing-proofs">
        <div className="container pricing-split">
          <div className="pricing-split-text">
            <div className="panel-eyebrow pricing-eyebrow">Proofs</div>
            <h2 className="pricing-h2">A flat price per proven block.</h2>
            <p>
              Proofs are optional. You pay a flat price for each block a proof covers, and never
              pay twice for the same block.
            </p>
          </div>
          <table className="pricing-table" data-testid="pricing-proofs-table">
            <thead>
              <tr>
                <th scope="col">Chain</th>
                <th scope="col">Per proven block</th>
                <th scope="col">Per 1,000 blocks</th>
              </tr>
            </thead>
            <tbody>
              {proofPriceRows(prices).map((r) => (
                <tr key={r.chainId}>
                  <th scope="row">{r.chain}</th>
                  <td>{r.perBlock}</td>
                  <td>{r.perThousandBlocks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* In a portal: inside the card, the dialog would light the card's hover border. */}
      {talking &&
        createPortal(
          <TalkToUsModal
            email={user?.email ?? ''}
            account={user?.email ?? user?.wallet_address ?? undefined}
            onClose={closeTalk}
          />,
          document.body,
        )}
    </div>
  );
}
